import { toast } from 'react-toastify';
import type { ToastOptions } from 'react-toastify';
import { Picture } from '@ui';
import { crossWhite } from '@images';
import { isValidElement, type FC } from 'react';
import './notifications.scss';

interface NotificationContentProps {
  title?: string;
  message?: string;
  onClose?: () => void;
}

const NotificationContent: FC<NotificationContentProps> = ({
  title,
  message,
  onClose
}) => (
  <div className='NotificationContent' onClick={onClose}>
    <Picture
      className='NotificationContent__cross'
      icon={crossWhite}
      height='18'
    />
    {title && <div className='NotificationContent__title'>{title}</div>}
    {message && (
      <div
        className='NotificationContent__text'
        dangerouslySetInnerHTML={{ __html: message }}
      />
    )}
  </div>
);

const settingsToast: ToastOptions<unknown> = {
  position: 'top-right',
  closeButton: false,
  autoClose: 2000,
  hideProgressBar: true,
  className: '!p-0 !bg-transparent w-[380px]'
};

function _showJSXnotification({
  title = '',
  comp,
  duration = 2000,
  position
}): void {
  const isPositionBottom = position === 'bottom';
  const toastComponent = ({ closeToast }) => (
    <div
      className='NotificationContent NotificationContent--centered'
      onClick={closeToast}
    >
      {!isPositionBottom && (
        <Picture
          className='NotificationContent__cross'
          icon={crossWhite}
          height='18'
        />
      )}
      {title && <div className='NotificationContent__title'>{title}</div>}
      <div className='NotificationContent__text'>{comp}</div>
      {isPositionBottom && (
        <Picture
          className='NotificationContent__cross NotificationContent__cross--centered'
          icon={crossWhite}
          height={20}
          width={20}
        />
      )}
    </div>
  );

  toast(toastComponent, {
    ...settingsToast,
    autoClose: duration,
    position: isPositionBottom ? 'bottom-right' : 'top-right'
  });

  // store.addNotification({
  //   ...settings,
  //   ...(isPositionBottom
  //     ? {
  //         insert: 'bottom',
  //         container: 'bottom-right'
  //       }
  //     : {}),
  //   dismiss: {
  //     ...settings.dismiss,
  //     duration
  //   },
  //   content: (
  //     <div className='NotificationContent NotificationContent--centered'>
  //       {!isPositionBottom && (
  //         <Picture
  //           className='NotificationContent__cross'
  //           icon={crossWhite}
  //           height='18'
  //         />
  //       )}
  //       {title && <div className='NotificationContent__title'>{title}</div>}
  //       <div className='NotificationContent__text'>{comp}</div>
  //       {isPositionBottom && (
  //         <Picture
  //           className='NotificationContent__cross NotificationContent__cross--centered'
  //           icon={crossWhite}
  //           height={20}
  //           width={20}
  //         />
  //       )}
  //     </div>
  //   )
  // });
}

function _showNotification(
  message = '',
  title = '',
  duration,
  toastId?: string
): void {
  if (
    message &&
    String(message) &&
    String(message)?.toLowerCase().includes('failed to fetch') &&
    title === 'Error'
  ) {
    return;
  }

  const toastComponent = ({ closeToast }) => (
    <NotificationContent title={title} message={message} onClose={closeToast} />
  );

  if (toastId && toast.isActive(toastId)) {
    toast.update(toastId, {
      render: toastComponent,
      autoClose: duration
    });
    return;
  }

  toast(toastComponent, {
    ...settingsToast,
    autoClose: duration,
    toastId
  });

  // store.addNotification({
  //   ...settings,
  //   dismiss: {
  //     ...settings.dismiss,
  //     duration
  //   },
  //   content: <NotificationContent title={title} message={message} />
  // });
}

export const jsxNotifications = ({
  comp,
  title = '',
  duration = 2000,
  position = 'top'
}): void => {
  if (isValidElement(comp)) {
    _showJSXnotification({ comp, title, duration, position });
  }
};

interface GiftNotificationProduct {
  id?: number | string | null;
  mainImage?: string | null;
  title?: string | null;
}

export const showGiftNotification = (gift?: GiftNotificationProduct): void => {
  const comp = (
    <a
      target='_blank'
      href={`${location.origin}/product/${gift?.id}`}
      className='flex items-start max-w-full p-1 bg-none'
      rel='noreferrer'
    >
      <div className='flex-shrink min-w-[2.5rem] w-10 h-10 mr-2 overflow-hidden rounded-lg'>
        <img
          className='object-contain w-full h-full'
          src={gift?.mainImage ?? ''}
          alt={`Подарок-${gift?.title}`}
        />
      </div>
      <div className='text-sm'>
        <div className='mb-1 '>Подарок к товару</div>
        <div className='line-clamp-1'>{gift?.title ?? 'Товар'}</div>
      </div>
    </a>
  );

  jsxNotifications({
    comp,
    title: 'Подарок добавлен',
    duration: 5000
  });
};

export const notifications = (
  error: any = '',
  title = 'Error',
  duration = 2000,
  toastId?: string
): void => {
  // ЭТАП 2 · SSR-guard: тосты — сущность браузера. На сервере (обработчики
  // ошибок API дёргают notifications из SSR-префетча) просто логируем и выходим.
  if (typeof document === 'undefined') {
    if (error) {
      console.error('[notifications:ssr]', title, error);
    }
    return;
  }
  if (!error || document.visibilityState !== 'visible') {
    return;
  }

  if (
    error[0]?.extensions &&
    error[0]?.message &&
    error[0].message.includes('timed out')
  ) {
    _showNotification(
      'Что-то с соединением. При повторении обратитесь к менеджеру',
      'Внимание',
      duration
    );
    return;
  }

  if (error?.[0]?.extensions) {
    _showNotification(error[0].message, 'Внимание', duration);
  } else {
    _showNotification(error, title, duration, toastId);
  }
};

const exceptionsMethods = ['addProductToCart', 'removeCartItem'];

export const isExceptionsNotification = (query): boolean =>
  exceptionsMethods.map(item => query.indexOf(item) !== -1).some(item => item);
